<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import { statusCount } from '../../tools/audit-helpers';
import { useAuditsStore, type StoreAudit } from '@/stores/audits';
import { useToast } from '@/shared/composables/useToast';
import type { Audit } from '../../types';

const store = useAuditsStore();
const router = useRouter();
const { toast } = useToast();

async function open(id: string): Promise<void> {
  await store.select(id);
  router.push('/collect');
}

async function remove(id: string): Promise<void> {
  await store.remove(id);
}

// List rows carry no `document`, so fall back to the server-denormalized
// criticalCount when the criteria map hasn't been loaded yet (R5).
function chipMeta(a: StoreAudit): string {
  const crit = a.criteria ? statusCount(a as Audit, 'critical') : a.criticalCount ?? 0;
  if (crit) return crit + '⚑';
  return (a.tier ?? 'p').charAt(0).toUpperCase();
}

// One-time migration affordance (Q3): only shown while legacy localStorage
// audits are present, hidden once imported.
const showImport = ref(false);
onMounted(() => {
  try {
    showImport.value = !!localStorage.getItem('ca:index');
  } catch {
    showImport.value = false;
  }
});

async function importLocal(): Promise<void> {
  const n = await store.importLocalAudits();
  showImport.value = false;
  toast(n ? `Imported ${n} local audit${n === 1 ? '' : 's'} ✓` : 'No local audits to import.');
}
</script>

<template>
  <div class="border-b px-[18px] py-4">
    <div class="mb-[11px] font-mono text-[10px] uppercase tracking-[0.16em] text-dim-2">Saved audits</div>
    <div class="flex flex-col gap-1 pt-[6px]">
      <div
        v-if="!store.audits.length"
        class="p-1 text-xs text-dim-2"
      >
        None yet.
      </div>
      <div
        v-for="a in store.audits"
        :key="a.id"
        class="group flex cursor-pointer items-center gap-[9px] rounded-sm border bg-popover px-[11px] py-[9px] text-[12.5px] transition-all duration-150"
        :class="a.id === store.currentId ? 'border-primary-dim' : 'border-transparent hover:border-input'"
        @click="open(a.id)"
      >
        <span class="flex-1 truncate font-semibold">{{ a.name }}</span>
        <span class="font-mono text-[9.5px] text-dim-2">{{ chipMeta(a) }}</span>
        <button
          type="button"
          class="px-[2px] text-[15px] text-dim-2 opacity-0 transition-all duration-150 hover:text-crit group-hover:opacity-100"
          @click.stop="remove(a.id)"
        >
          ×
        </button>
      </div>
    </div>

    <button
      v-if="showImport"
      type="button"
      data-testid="import-local-audits"
      class="mt-2.5 w-full rounded-sm border border-dashed border-input px-[11px] py-[7px] text-left font-mono text-[10.5px] text-dim-2 transition-colors duration-150 hover:border-dim-2 hover:text-muted-foreground"
      @click="importLocal"
    >
      ↑ Import local audits
    </button>
  </div>
</template>
